import type { Faro, Meta } from '@grafana/faro-web-sdk'
import type { MontillaLoadConfig, LocationApiResponse } from './types'
import type { MontillaContext } from './types/destination'
import type { SourceDomain } from './types/mappings'
import type { MappingsRegistry } from './utils/destinations/mappings/registry'
import { EventBus } from './event-bus'
import { FaroDestination } from './destinations/faro/faro-destination'
import { globalSingleton } from './utils/global-singleton'

/** Ver `MontillaState.clientConfigSource`. */
export type ClientConfigSource = 'ssr' | 'fetch' | 'none'

export interface MontillaState {
  initialized: boolean
  eventBus: EventBus
  context: MontillaContext | null
  config: MontillaLoadConfig | null
  locationPromise: Promise<LocationApiResponse | null> | null
  /**
   * Source domain stamped onto every dispatched `EventPayload.domain`.
   * Set from `MontillaLoadConfig.source` on load (default `'browser'`).
   */
  source: SourceDomain

  /**
   * De onde veio a client config desta página. `'ssr'` é o host tendo
   * resolvido o documento antes (e mandado em `MontillaLoadConfig.clientConfig`),
   * `'fetch'` é o browser tendo ido buscar, `'none'` é não ter havido nenhuma.
   * Só existe para diagnóstico — a extensão mostra, o SDK não decide nada com
   * isto.
   */
  clientConfigSource: ClientConfigSource
  /**
   * Registry of `Destination` entries indexed by full versioned slug.
   * Populated at load time via `loadMappings()`. Destinations look up their
   * bundle by slug and check `(type, version)` before going live.
   */
  mappings: MappingsRegistry | null

  /**
   * @deprecated Use eventBus instead. Kept for backward compatibility.
   * Returns the Faro instance from FaroDestination.
   */
  get faro(): Promise<Faro>
}

export const state: MontillaState = globalSingleton<MontillaState>('state', () => {
  const eventBus = new EventBus()
  return {
    initialized: false,
    eventBus,
    context: null,
    config: null,
    locationPromise: null,
    source: 'browser',
    clientConfigSource: 'none',
    mappings: null,

    get faro(): Promise<Faro> {
      return eventBus.ready().then(() => {
        const faroDest = eventBus.getDestination<FaroDestination>('faro')
        const instance = faroDest?.getFaroInstance()
        if (!instance) {
          return new Promise<Faro>(() => undefined) // never resolves — no Faro destination
        }
        return instance
      })
    },
  }
})

// Backwards-compat shim for the previous module-level `eventBus` const.
// Anything that imported it directly will keep working without rewriting.
const eventBus = state.eventBus

/**
 * Synchronous accessor for the live Faro instance. Returns `null` when
 * Faro hasn't been initialized yet. Use this when you need to call
 * `faro.api.set*` from inside the api layer (api/page.ts, api/identify.ts).
 */
export function getFaro(): Faro | null {
  const faroDest = eventBus.getDestination<FaroDestination>('faro')
  return faroDest?.getFaroInstance() ?? null
}

/**
 * Synchronous accessor for the live Faro metas snapshot. Returns `null`
 * when Faro hasn't been initialized. Faro is the canonical metadata store
 * after the philosophy refactor — destinations and the canonical-view
 * resolver read user/session/page/view from here instead of `state.context`.
 */
export function getFaroMetas(): Meta | null {
  return getFaro()?.metas.value ?? null
}
